import { createAsyncThunk, createSlice, PayloadAction, Update } from "@reduxjs/toolkit"
import axios from "axios";
import { IProductQuestionnaire, IProductQuestionnaireResponse } from "../../../Types/Products/IProductQuestionnaire";
import { axiosInstance } from "../../../Authentication/AxiosInterceptor";
import { IQuestionnaireQuestion, QuestionCoachInsights, QuestionEvidenceSource } from "../../../Types/Products/IQuestionnaireQuestion";
import { IQuestionnaireSection } from "../../../Types/Products/IQuestionnaireSection";
import { getCriteriaIndex, getCriteriaIndexById, getQuestionIndex, getSectionIndex, getSectionIndexById, ICriteriaItem } from "../../../Types/Products/ICriteriaItemData";
import { IGrade } from "../../../Types/IGrade";
import { RootState } from "../Store";
import { ICompletionStatus } from "../../../Types/ICompletionStatus";
import { stat } from "fs";
import { createSelector } from '@reduxjs/toolkit';
import { useRef } from "react";
import { IQuestionnaireSectionResults } from "../../../Types/Products/IQuestionnaireSectionResults";
import { FieldType, IDynamicField, SpecialField } from "../../../Types/Products/IDynamicField";
import type { AreaKpiSummary } from "../../../Types/KPI/IAreaKpiSummary";
import { UpdateDynamicFieldContentById, UpdateEvaluateFieldByCriteriaIdAndActivityId } from "../../../API/Evaluate/Evaluate";
import {
  UpdateNotesPermissions,
  UpdateNotesPermissionsRequest,
} from "../../../API/Utils/NotesPermissions";
import { patchRestrictClientAdminQuestionnaireAccess } from "../../../API/Utils/RestrictClientAdminQuestionnaireAccess";
import { NotesPermission } from "../../../Types/Products/IProductQuestionnaire";
import { SummariseResponse } from "../../../Types/Evaluate/SummariseResponse";
import { UpdateCriteriaDateRequest } from "../../../Types/Evaluate/UpdateCriteriaDateRequest";
import { EvaluateRolePermission } from "../../../Types/Roles/RolePermission";
import {  isSetDate, toDate, UNSET_DATE } from "../../../Helpers/Dates";
import { Comment } from "../../../Components/Utils/CommentsPopup/CommentsPopup";
import { getClient, getAiPowered, getMiaCoachEnabled } from "./client";
import { ProductType } from "../../../Types/ProductType";
import { toast } from "react-toastify";
import { CommentType, CreateCommentRequest } from "../../../Types/CreateCommentRequest";
import { UpdateLowerFieldsRequest, UpdateLowerFieldType } from "../../../Types/Develop/UpdateLowerFieldRequest";
import { IDrawerSection } from "../../../Types/Drawer/ISectionsDrawerItem";
import { sendMcpMessageStreaming } from "../../../API/McpChat/McpChat";
import { McpStreamEvent } from "../../../Types/McpChat/McpChat";
import { isUserFacingProgressEvent, appendProgressStep } from "../../../Utils/mcpProgress";
import { FileData } from "../../../Components/Utils/FileItem/FileItem";
import type {
  PrefetchedConversationFile,
  PrefetchedLinkedEvidenceFile,
} from "../../../Types/Evaluate/AreaCriteriaContextData";
import {
    buildGenerateAnswersPrompt,
    isInsufficientEvidenceAnswer,
    looksLikeJsonAnswerPayload,
    mapAiAnswersToQuestions,
    parseAiAnswerPayload,
    PARSE_FALLBACK_SKIP_REASON,
    snapshotQuestionAnswers,
} from "../../../Utils/aiAnswers";
import {
    buildCoachPrompt,
    CoachFeedback,
    normalizeCoachQuality,
    parseCoachFeedback,
} from "../../../Utils/aiCoach";
import {
    buildSummariseAssessmentPrompt,
    parseSummariseAssessmentAnswer,
} from "../../../Utils/aiAssessment";
import { CurateResource } from "../../../Types/Curate/CurateResource";
import { normalizeCurateResources } from "../../../Utils/curateResources";
import { fieldsInExportOrder } from "../../../Utils/Export/exporters/exportHelpers";
import { beginPendingSave } from "../../../Hooks/useUnsavedChangesWarning";

let updateTimer: NodeJS.Timeout | null = null; // Declare the timer variable
let endPendingDebouncedSave: (() => void) | null = null;

const debounceDelay = 500;

function scheduleDebouncedQuestionnaireSave(run: () => Promise<void>) {
  if (updateTimer) {
    clearTimeout(updateTimer);
  } else {
    endPendingDebouncedSave = beginPendingSave();
  }

  updateTimer = setTimeout(async () => {
    updateTimer = null;
    try {
      await run();
    } catch (error) {
      console.error(error);
    } finally {
      endPendingDebouncedSave?.();
      endPendingDebouncedSave = null;
    }
  }, debounceDelay);
}

function normalizePrefetchedComment(raw: Record<string, unknown>): Comment {
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    DateMade: new Date(String(raw.DateMade ?? raw.dateMade ?? '')),
    CommentText: String(raw.CommentText ?? raw.commentText ?? ''),
    Commenter: String(raw.Commenter ?? raw.commenter ?? ''),
    Editable: Boolean(raw.Editable ?? raw.editable ?? false),
    ProgressStatus: raw.ProgressStatus as Comment['ProgressStatus'],
  };
}

function normalizePrefetchedLinkedEvidence(raw: Record<string, unknown>): PrefetchedLinkedEvidenceFile {
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    linkId: String(raw.linkId ?? raw.LinkId ?? ''),
    fileName: String(raw.fileName ?? raw.FileName ?? ''),
    portfolioId: String(raw.portfolioId ?? raw.PortfolioId ?? ''),
    folderId: String(raw.folderId ?? raw.FolderId ?? ''),
    isAvailable: raw.isAvailable !== false && raw.IsAvailable !== false,
  };
}

function normalizePrefetchedConversationFile(raw: Record<string, unknown>): PrefetchedConversationFile {
  return {
    fileId: String(raw.fileId ?? raw.FileId ?? ''),
    fileName: String(raw.fileName ?? raw.FileName ?? ''),
    extension: String(raw.extension ?? raw.Extension ?? ''),
    fileSize: Number(raw.fileSize ?? raw.FileSize ?? 0),
    conversationId: String(raw.conversationId ?? raw.ConversationId ?? ''),
    uploadedAt: String(raw.uploadedAt ?? raw.UploadedAt ?? ''),
    uploadedBy: String(raw.uploadedBy ?? raw.UploadedBy ?? ''),
  };
}

function normalizeCommentsByCriteriaId(
  input: Record<string, unknown> | undefined
): Record<string, Comment[]> {
  if (!input) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(input).map(([criteriaId, comments]) => [
      criteriaId,
      (Array.isArray(comments) ? comments : []).map((comment) =>
        normalizePrefetchedComment(comment as Record<string, unknown>)
      ),
    ])
  );
}

function normalizeLinkedEvidenceByCriteriaId(
  input: Record<string, unknown> | undefined
): Record<string, PrefetchedLinkedEvidenceFile[]> {
  if (!input) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(input).map(([criteriaId, files]) => [
      criteriaId,
      (Array.isArray(files) ? files : []).map((file) =>
        normalizePrefetchedLinkedEvidence(file as Record<string, unknown>)
      ),
    ])
  );
}

function normalizeConversationFilesByCriteriaId(
  input: Record<string, unknown> | undefined
): Record<string, PrefetchedConversationFile[]> {
  if (!input) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(input).map(([criteriaId, files]) => [
      criteriaId,
      (Array.isArray(files) ? files : []).map((file) =>
        normalizePrefetchedConversationFile(file as Record<string, unknown>)
      ),
    ])
  );
}

function applyActiveCriteriaComments(state: QuestionnaireState, criteriaId: string) {
  state.comments = state.commentsByCriteriaId[criteriaId] ?? [];
}

function applyActiveCriteriaResources(state: QuestionnaireState) {
  const section = state.questionnaire?.questionnaireSections?.find(
    (item) => item.id === state.activeSectionId
  );
  const criteria = section?.criteria?.find((item) => item.id === state.activeCriteriaId);
  state.recommendedCurateResources = criteria?.recommendedCurateResources ?? [];
}

export type GetQuestionnaireByAreaIdArg =
  | string
  | {
      areaId: string;
      /** Bypass in-flight / already-loaded deduplication (e.g. after KPI or permission changes). */
      force?: boolean;
    };

function resolveGetQuestionnaireByAreaIdArg(arg: GetQuestionnaireByAreaIdArg): {
  areaId: string;
  force: boolean;
} {
  if (typeof arg === 'string') {
    return { areaId: arg, force: false };
  }
  return { areaId: arg.areaId, force: arg.force ?? false };
}

// Comment management async thunks
export const fetchCommentsByCriteriaId = createAsyncThunk(
    "questionnaire/fetchCommentsByCriteriaId",
    async (criteriaId: string, thunkAPI) => {
        try {
            const response = await axiosInstance.get<Comment[]>(`/GetSummariseCommentsByCriteriaId/${criteriaId}/${CommentType.Comment}`);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const createComment = createAsyncThunk(
    "questionnaire/createComment",
    async ({ criteriaId, authorizationContextId, commentText }: { criteriaId: string; authorizationContextId: string; commentText: string }, thunkAPI) => {
        try {
            const request : CreateCommentRequest = {
              text: commentText,
              type: CommentType.Comment
            };

            const response = await axiosInstance.post<Comment>(`/CreateSummariseComment/${criteriaId}/${authorizationContextId}`, request);
            return response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const editComment = createAsyncThunk(
    "questionnaire/editComment",
    async ({ commentId, text }: { commentId: string; text: string }, thunkAPI) => {
        try {
            const request : CreateCommentRequest = {
              text: text,
              progressStatus: undefined,
              type: CommentType.Comment
            };

            await axiosInstance.post(`/EditSummariseComment/${commentId}`, request);
            return { commentId, text };
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const deleteComment = createAsyncThunk(
    "questionnaire/deleteComment",
    async (commentId: string, thunkAPI) => {
        try {
            await axiosInstance.get(`/DeleteSummariseComment/${commentId}`);
            return commentId;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export type CoachStatus = "loading" | "done" | "error";

interface QuestionnaireState {
    questionnaire: IProductQuestionnaire | undefined,
    areaKpiSummary: AreaKpiSummary | undefined,
    /** Area-level due date; separate from per-criteria completion dates. */
    areaDueDate: Date | undefined,
    activeSectionId: string,
    activeCriteriaId: string,
    rolePermission?: EvaluateRolePermission,
    comments: Comment[],
    commentsByCriteriaId: Record<string, Comment[]>,
    linkedEvidenceByCriteriaId: Record<string, PrefetchedLinkedEvidenceFile[]>,
    conversationFilesByCriteriaId: Record<string, PrefetchedConversationFile[]>,
    commentsLoading: boolean,
    isViewingResults: boolean,
    isGeneratingAnswersFromEvidence: boolean,
    generateAnswersProgressSteps: string[],
    isAcceptingAiAnswers: boolean,
    aiAnswerSnapshot: Record<string, string> | null,
    aiGeneratedAnswers: Record<string, string> | null,
    aiAnsweredQuestionIds: string[],
    aiSkipReasonsByQuestionId: Record<string, string>,
    aiEvidenceSources: QuestionEvidenceSource[],
    coachFeedbackByQuestionId: Record<string, CoachFeedback>,
    coachStatusByQuestionId: Record<string, CoachStatus>,
    coachProgressByQuestionId: Record<string, string>,
    coachConversationId: string | null,
    coachedAnswerTextByQuestionId: Record<string, string>,
    recommendedCurateResources: CurateResource[],
    /** Area id currently being loaded by getQuestionnaireByAreaId (dedupes concurrent requests). */
    questionnaireLoadingAreaId?: string,
}

const initialState: QuestionnaireState = {
    questionnaire: undefined,
    areaKpiSummary: undefined,
    areaDueDate: undefined,
    activeSectionId: "",
    activeCriteriaId: "",
    rolePermission: undefined,
    comments: [],
    commentsByCriteriaId: {},
    linkedEvidenceByCriteriaId: {},
    conversationFilesByCriteriaId: {},
    commentsLoading: false,
    isViewingResults: false,
    isGeneratingAnswersFromEvidence: false,
    generateAnswersProgressSteps: [],
    isAcceptingAiAnswers: false,
    aiAnswerSnapshot: null,
    aiGeneratedAnswers: null,
    aiAnsweredQuestionIds: [],
    aiSkipReasonsByQuestionId: {},
    aiEvidenceSources: [],
    coachFeedbackByQuestionId: {},
    coachStatusByQuestionId: {},
    coachProgressByQuestionId: {},
    coachConversationId: null,
    coachedAnswerTextByQuestionId: {},
    recommendedCurateResources: [],
}

export interface UpdateQuestionnaireSectionStatusPayload{
    sectionId: string,
    status: ICompletionStatus
}

export interface UpdateFieldContentPayload {
    field: IDynamicField;
    criteriaId: string;
    activityId: string;
    //specialField: SpecialField;
}

export interface UnlockFieldPayload {
    criteriaId: string;
    activityId: string;
}

// Using axios and try...catch in createAsyncThunk

export const updateQuestionnaireScope = createAsyncThunk(
  "questionnaire/UpdateQuestionnaireScope",
  async ({ areaId, scope }: { areaId: string; scope: string }, thunkApi) => {
    const state = thunkApi.getState() as RootState;

    if (!state.questionnaire.rolePermission?.editScope) {
      return thunkApi.rejectWithValue("no permission to edit scope");
    }

    // Debounce the API call, but return the scope immediately so UI updates
    scheduleDebouncedQuestionnaireSave(async () => {
      await axiosInstance.patch(`UpdateQuestionnaireScope/${areaId}`, scope);
    });

    return scope; // update store optimistically
  }
);

export const UpdateCriteriaGrade = createAsyncThunk(
  "area/UpdateCriteriaGrade",
  async ({gradeId, criteriaId, gradeProfileId, questionnaireId}: {gradeId: string, criteriaId: string, gradeProfileId: string, questionnaireId: string}, thunkAPI) => {
    try {
      // Validate that the gradeId is valid for the profile
      const state = thunkAPI.getState() as RootState;
      const questionnaire = state.questionnaire.questionnaire;
      
      if (questionnaire && questionnaire.gradeOptions) {
        const isValidGrade = questionnaire.gradeOptions.some(option => option.value === gradeId);
        if (!isValidGrade) {
          return thunkAPI.rejectWithValue("Invalid grade for this profile");
        }
      }

      const request: UpdateLowerFieldsRequest = {
        areaId: questionnaireId,
        criteriaId,
        data: gradeId,
        listData: [],
        gradeProfileId,
        updateType: UpdateLowerFieldType.UpdateGrade
      };

      const response = await axiosInstance.post(`UpdateCriteriaLowerField`, request);
      return {gradeId, criteriaId, gradeProfileId, questionnaireId}; // Return the payload to update the state
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while updating the grade';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export type UpdateCriteriaLowerFieldPayload = {
  criteriaId: string;
  updateType: UpdateLowerFieldType.UpdatePerson | UpdateLowerFieldType.UpdateDropdown | UpdateLowerFieldType.UpdatePicklist | UpdateLowerFieldType.UpdateTags;
  data?: string;
  listData?: string[];
};

export const UpdateCriteriaLowerField = createAsyncThunk(
  "questionnaire/UpdateCriteriaLowerField",
  async (payload: UpdateCriteriaLowerFieldPayload, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const questionnaire = state.questionnaire.questionnaire;
      const questionnaireId = questionnaire?.id ?? "";
      const gradeProfileId = questionnaire?.gradeProfileId ?? "";

      const request: UpdateLowerFieldsRequest = {
        areaId: questionnaireId,
        criteriaId: payload.criteriaId,
        data: payload.data ?? "",
        listData: payload.listData ?? [],
        gradeProfileId,
        updateType: payload.updateType,
      };

      await axiosInstance.post(`UpdateCriteriaLowerField`, request);
      return payload;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "An error occurred while updating the field";
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const UpdateCriteriaDate = createAsyncThunk(
  "area/UpdateCriteriaDate",
  async (body: UpdateCriteriaDateRequest, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`UpdateCriteriaDateByCriteriaIdAndSectionIdAndActivityId/${body.criteriaId}/${body.sectionId}/${body.activityId}`, body.date);
      return body; // Return the payload to update the state
    } catch (error: any) {
      // Extract a meaningful error message from the error object
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while updating the date';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const UpdateAreaDueDate = createAsyncThunk(
  "area/UpdateAreaDueDate",
  async ({ areaId, dueDate }: { areaId: string; dueDate: Date }, thunkAPI) => {
    try {
      await axiosInstance.patch(`UpdateAreaDueDate/${areaId}`, dueDate);
      return dueDate;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while updating the due date';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Using axios and try...catch in createAsyncThunk
export const getQuestionnaireByAreaId = createAsyncThunk(
    "questionnaire/GetQuestionnaireByAreaId",
    async (arg: GetQuestionnaireByAreaIdArg) => {
        const { areaId } = resolveGetQuestionnaireByAreaIdArg(arg);
        const response = await axiosInstance.get(`GetQuestionnaireByAreaId/${areaId}`);
        return response.data as IProductQuestionnaireResponse;
    },
    {
        condition: (arg, { getState }) => {
            const { areaId, force } = resolveGetQuestionnaireByAreaIdArg(arg);
            if (force) {
                return true;
            }

            const qState = (getState() as RootState).questionnaire;
            if (qState.questionnaireLoadingAreaId === areaId) {
                return false;
            }

            if (qState.questionnaire?.id === areaId && qState.rolePermission) {
                return false;
            }

            return true;
        },
    }
);

export const updateQuestionnaireQuestion = createAsyncThunk("questionnaire/UpdateQuestionnaireQuestion", async (question: IQuestionnaireQuestion, thunkApi) => {
    const {questionnaire} = thunkApi.getState() as RootState;

    if(!questionnaire.rolePermission?.editQuestionnaireContent){
        return question; // If the user does not have permission, return the question without updating
    }

    const response = await axiosInstance.post(`UpdateQuestionnaireQuestion`, question);
    await axiosInstance.get(`UnlockResource/${question.id}`);

    return question;
});

export interface GenerateAnswersFromEvidencePayload {
    token: string;
    clientId: string;
    areaId: string;
    criteriaId: string;
    questions: IQuestionnaireQuestion[];
    evidenceFiles: FileData[];
    criteriaTitle: string;
    sectionTitle: string;
    conversationId?: string;
    /** Criteria id — used as MCP contextId so the conversation resumes for this criteria + user. */
    contextId?: string;
}

export interface GenerateAnswersFromEvidenceResult {
    aiAnswerSnapshot: Record<string, string>;
    aiGeneratedAnswers: Record<string, string>;
    aiAnsweredQuestionIds: string[];
    aiEvidenceSources: QuestionEvidenceSource[];
    answeredCount: number;
    totalQuestions: number;
    missingQuestionNumbers: number[];
    skipReasonsByQuestionId: Record<string, string>;
    sourcesByQuestionId: Record<string, QuestionEvidenceSource[]>;
    /** True when the model returned only blanks + skip reasons (no drafts). */
    allSkippedWithReasons?: boolean;
    parseWarning?: boolean;
}

const toEvidenceSources = (evidenceFiles: FileData[]): QuestionEvidenceSource[] =>
    evidenceFiles.map((file) => ({
        id: file.id,
        fileName: file.fileName,
        portfolioId: file.portfolioId,
        folderId: file.folderId,
        source: file.source,
    }));

// Friendly, user-facing labels for known MCP tools. Keyed by the tool name
// normalised to lowercase with separators removed, so it matches regardless
// of PascalCase vs snake_case naming on the server.
const toolProgressLabels: Record<string, string> = {
    getuserlibraryfiles: "Getting your linked files",
    getconversationfiles: "Getting your uploaded files",
    retrievelibraryfilescontent: "Reading your evidence",
    getdiagnosticcriteriacontext: "Reviewing the criteria guidance",
};

const normaliseToolName = (toolName: string): string =>
    toolName.replace(/[_\-\s]/g, "").toLowerCase();

// Fallback for unknown tools: "GetLinkedEvidenceContent" -> "Getting linked evidence content"
const humaniseToolName = (toolName: string): string => {
    const friendly = toolProgressLabels[normaliseToolName(toolName)];
    if (friendly) {
        return friendly;
    }

    // Turn a leading verb into its -ing form for a natural "doing" feel.
    const gerunds: Record<string, string> = {
        get: "getting",
        retrieve: "retrieving",
        fetch: "fetching",
        list: "listing",
        read: "reading",
        search: "searching",
        find: "finding",
        update: "updating",
        create: "creating",
        generate: "generating",
        check: "checking",
    };

    const words = toolName
        .replace(/[_-]/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .toLowerCase()
        .trim()
        .split(" ");

    if (words[0] && gerunds[words[0]]) {
        words[0] = gerunds[words[0]];
    }

    const sentence = words.join(" ");
    return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

const streamEventToProgressText = (event: McpStreamEvent, answerLength: number): string | null => {
    if (!isUserFacingProgressEvent(event)) {
        return null;
    }

    switch (event.type) {
        case "status":
        case "activity":
            return event.text ?? null;
        case "tool":
            if (event.text) {
                return event.text;
            }
            return event.toolName ? `${humaniseToolName(event.toolName)}...` : "Working...";
        case "delta":
            return answerLength > 0 ? "Writing answers..." : null;
        default:
            return null;
    }
};

const coachStreamEventToProgressText = (event: McpStreamEvent, answerLength: number): string | null => {
    if (!isUserFacingProgressEvent(event)) {
        return null;
    }

    switch (event.type) {
        case "status":
        case "activity":
            return event.text ?? null;
        case "tool":
            if (event.text) {
                return event.text;
            }
            return event.toolName ? `${humaniseToolName(event.toolName)}...` : "Working...";
        case "delta":
            return answerLength > 0 ? "Writing feedback..." : null;
        default:
            return null;
    }
};

export const generateAnswersFromEvidence = createAsyncThunk(
    "questionnaire/generateAnswersFromEvidence",
    async (payload: GenerateAnswersFromEvidencePayload, thunkApi) => {
        const state = thunkApi.getState() as RootState;

        if (!state.questionnaire.rolePermission?.editQuestionnaireContent) {
            return thunkApi.rejectWithValue("You do not have permission to edit questionnaire answers.");
        }

        if (!getAiPowered(state)) {
            return thunkApi.rejectWithValue("AI features are not enabled for this client.");
        }

        const {
            token,
            clientId,
            areaId,
            criteriaId,
            questions,
            evidenceFiles,
            criteriaTitle,
            sectionTitle,
            conversationId,
            contextId,
        } = payload;

        if (questions.length === 0) {
            return thunkApi.rejectWithValue("No diagnostic questions found for this criteria.");
        }

        if (evidenceFiles.length === 0) {
            return thunkApi.rejectWithValue("Please select at least one evidence file.");
        }

        const aiAnswerSnapshot = snapshotQuestionAnswers(questions);
        const aiEvidenceSources = toEvidenceSources(evidenceFiles);
        const prompt = buildGenerateAnswersPrompt({
            areaId,
            criteriaId,
            criteriaTitle,
            sectionTitle,
            questions,
            evidenceFiles,
        });

        // Stream the agent's progress (tool calls, status updates, answer chunks)
        // so the UI can show what Mia is doing in real time.
        let answerLength = 0;
        const response = await sendMcpMessageStreaming(
            token,
            prompt,
            clientId,
            (event) => {
                if (event.type === "delta") {
                    answerLength += event.text?.length ?? 0;
                }
                const progressText = streamEventToProgressText(event, answerLength);
                if (progressText) {
                    thunkApi.dispatch(pushGenerateAnswersProgressStep(progressText));
                }
            },
            conversationId,
            contextId,
            undefined,
            {
                mode: "Diagnostic",
                allowedFileIds: evidenceFiles.map((file) => file.id),
            }
        );
        const parsedPayload = parseAiAnswerPayload(response.answer);
        const parsedAnswers = parsedPayload?.answers ?? null;

        if (!parsedAnswers) {
            const fallbackAnswer = response.answer.trim();
            if (
                !fallbackAnswer ||
                isInsufficientEvidenceAnswer(fallbackAnswer) ||
                looksLikeJsonAnswerPayload(fallbackAnswer)
            ) {
                return thunkApi.rejectWithValue(
                    "Evidence was not sufficient to draft answers for these questions. Try adding more relevant files."
                );
            }

            const firstQuestion = questions[0];
            const skipReasonsByQuestionId = questions.slice(1).reduce(
                (acc, question) => {
                    acc[question.id] = PARSE_FALLBACK_SKIP_REASON;
                    return acc;
                },
                {} as Record<string, string>
            );
            const fallbackResult: GenerateAnswersFromEvidenceResult = {
                aiAnswerSnapshot,
                aiGeneratedAnswers: { [firstQuestion.id]: fallbackAnswer },
                aiAnsweredQuestionIds: [firstQuestion.id],
                aiEvidenceSources,
                answeredCount: 1,
                totalQuestions: questions.length,
                missingQuestionNumbers: questions.slice(1).map((_, index) => index + 2),
                skipReasonsByQuestionId,
                sourcesByQuestionId: { [firstQuestion.id]: aiEvidenceSources },
                parseWarning: true,
            };

            await persistDraftAnswers(
                thunkApi,
                questions,
                fallbackResult.aiGeneratedAnswers,
                fallbackResult.aiAnsweredQuestionIds,
                fallbackResult.sourcesByQuestionId
            );

            return fallbackResult;
        }

        const { answersByQuestionId, answeredQuestionIds, skipReasonsByQuestionId, sourceIdsByQuestionId } = mapAiAnswersToQuestions(
            questions,
            parsedAnswers,
            parsedPayload?.skipReasons,
            parsedPayload?.sources
        );

        const sourcesByQuestionId = resolveSourcesByQuestionId(
            answeredQuestionIds,
            sourceIdsByQuestionId,
            aiEvidenceSources
        );

        if (answeredQuestionIds.length === 0) {
            const skippedResult: GenerateAnswersFromEvidenceResult = {
                aiAnswerSnapshot,
                aiGeneratedAnswers: {},
                aiAnsweredQuestionIds: [],
                aiEvidenceSources,
                answeredCount: 0,
                totalQuestions: questions.length,
                missingQuestionNumbers: questions.map((_, index) => index + 1),
                skipReasonsByQuestionId,
                sourcesByQuestionId: {},
                allSkippedWithReasons: true,
            };
            return skippedResult;
        }

        const missingQuestionNumbers = questions
            .map((question, index) => ({ question, num: index + 1 }))
            .filter(({ question }) => !answeredQuestionIds.includes(question.id))
            .map(({ num }) => num);

        await persistDraftAnswers(
            thunkApi,
            questions,
            answersByQuestionId,
            answeredQuestionIds,
            sourcesByQuestionId
        );

        const successResult: GenerateAnswersFromEvidenceResult = {
            aiAnswerSnapshot,
            aiGeneratedAnswers: answersByQuestionId,
            aiAnsweredQuestionIds: answeredQuestionIds,
            aiEvidenceSources,
            answeredCount: answeredQuestionIds.length,
            totalQuestions: questions.length,
            missingQuestionNumbers,
            skipReasonsByQuestionId,
            sourcesByQuestionId,
        };
        return successResult;
    }
);

// Persist AI drafts onto question documents without overwriting the current answer.
const persistDraftAnswers = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    thunkApi: { dispatch: (action: any) => any },
    questions: IQuestionnaireQuestion[],
    draftsByQuestionId: Record<string, string>,
    answeredQuestionIds: string[],
    sourcesByQuestionId: Record<string, QuestionEvidenceSource[]>
) => {
    await Promise.allSettled(
        answeredQuestionIds.map((questionId) => {
            const question = questions.find((q) => q.id === questionId);
            const draftAnswer = draftsByQuestionId[questionId];
            if (!question || draftAnswer === undefined) {
                return Promise.resolve();
            }

            const sources = sourcesByQuestionId[questionId] ?? [];
            return thunkApi
                .dispatch(
                    updateQuestionnaireQuestion({
                        ...question,
                        draftAnswer,
                        draftEvidenceSources:
                            sources.length > 0 ? sources : question.draftEvidenceSources,
                    })
                )
                .unwrap();
        })
    );
};

const resolveSourcesByQuestionId = (
    answeredQuestionIds: string[],
    sourceIdsByQuestionId: Record<string, string[]>,
    allSources: QuestionEvidenceSource[]
): Record<string, QuestionEvidenceSource[]> => {
    const byId = new Map(allSources.map((source) => [source.id, source]));
    const result: Record<string, QuestionEvidenceSource[]> = {};
    answeredQuestionIds.forEach((questionId) => {
        const ids = sourceIdsByQuestionId[questionId] ?? [];
        const matched = ids
            .map((id) => byId.get(id))
            .filter((source): source is QuestionEvidenceSource => !!source);
        result[questionId] = matched.length > 0 ? matched : allSources;
    });
    return result;
};

export interface FetchCoachFeedbackPayload {
    token: string;
    clientId: string;
    areaId: string;
    criteriaId: string;
    criteriaTitle: string;
    fullCriteria: string;
    questionId: string;
    questionText: string;
    answerText: string;
    evidenceFiles: FileData[];
    conversationId?: string;
}

export interface FetchCoachFeedbackResult {
    questionId: string;
    answerText: string;
    feedback: CoachFeedback;
    conversationId: string | null;
    requestSeq: number;
}

// Latest-wins guard: fast retyping can have multiple coach requests in flight
// for the same question; only the newest one is allowed to write feedback.
const coachRequestSeqByQuestionId: Record<string, number> = {};

const emptyCoachFeedback: CoachFeedback = {
    quality: "empty",
    summary: "",
    missing: [],
    highlights: [],
    evidenceConflicts: [],
};

export const fetchCoachFeedback = createAsyncThunk(
    "questionnaire/fetchCoachFeedback",
    async (payload: FetchCoachFeedbackPayload, thunkApi) => {
        const state = thunkApi.getState() as RootState;

        if (!state.questionnaire.rolePermission?.editQuestionnaireContent) {
            return thunkApi.rejectWithValue("no permission");
        }

        if (!getAiPowered(state)) {
            return thunkApi.rejectWithValue("AI features are not enabled for this client.");
        }

        if (!getMiaCoachEnabled(state)) {
            return thunkApi.rejectWithValue("Mia Coach is turned off for this client.");
        }

        const { token, clientId, areaId, criteriaId, criteriaTitle, fullCriteria, questionId, questionText, answerText, evidenceFiles, conversationId: payloadConversationId } = payload;

        const requestSeq = (coachRequestSeqByQuestionId[questionId] ?? 0) + 1;
        coachRequestSeqByQuestionId[questionId] = requestSeq;

        const conversationId = payloadConversationId ?? state.questionnaire.coachConversationId ?? undefined;

        // Empty answers don't need an AI round-trip.
        if (!answerText.trim()) {
            return {
                questionId,
                answerText,
                feedback: emptyCoachFeedback,
                conversationId: conversationId ?? null,
                requestSeq,
            } satisfies FetchCoachFeedbackResult;
        }

        const prompt = buildCoachPrompt({
            areaId,
            criteriaId,
            criteriaTitle,
            fullCriteria,
            questionText,
            answerText,
            evidenceFiles,
        });

        const allowedFileIds = evidenceFiles.map((file) => file.id).filter(Boolean);

        const streamCoach = async (message: string, existingConversationId?: string) => {
            let answerLength = 0;
            return sendMcpMessageStreaming(
                token,
                message,
                clientId,
                (event) => {
                    if (event.type === "delta") {
                        answerLength += event.text?.length ?? 0;
                    }
                    const progressText = coachStreamEventToProgressText(event, answerLength);
                    if (progressText) {
                        thunkApi.dispatch(setCoachProgress({ questionId, text: progressText }));
                    }
                },
                existingConversationId,
                undefined,
                undefined,
                {
                    mode: "Diagnostic",
                    ...(allowedFileIds.length > 0 ? { allowedFileIds } : {}),
                }
            );
        };

        let response = await streamCoach(prompt, conversationId);
        let parsed = parseCoachFeedback(response.answer);
        if (!parsed) {
            response = await streamCoach(
                "Return only the JSON object for the coach review. No commentary and no extra text.",
                response.conversationId || conversationId
            );
            parsed = parseCoachFeedback(response.answer);
        }

        if (!parsed) {
            return thunkApi.rejectWithValue({ questionId, requestSeq, message: "Could not parse coach feedback." });
        }

        const feedback = normalizeCoachQuality(parsed);

        // Fire-and-forget persist so insights survive reloads; ignore lock conflicts.
        thunkApi
            .dispatch(persistCoachInsights({ questionId, feedback, answerText }))
            .catch(() => undefined);

        return {
            questionId,
            answerText,
            feedback,
            conversationId: response.conversationId || null,
            requestSeq,
        } satisfies FetchCoachFeedbackResult;
    }
);

const findQuestionInState = (
    state: QuestionnaireState,
    questionId: string
): { sectionIndex: number; criteriaIndex: number; questionIndex: number; question: IQuestionnaireQuestion } | null => {
    const sections = state.questionnaire?.questionnaireSections;
    if (!sections) {
        return null;
    }

    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
        const section = sections[sectionIndex];
        for (let criteriaIndex = 0; criteriaIndex < section.criteria.length; criteriaIndex++) {
            const criteria = section.criteria[criteriaIndex];
            const questionIndex = criteria.questions.findIndex((q) => q.id === questionId);
            if (questionIndex !== -1) {
                return {
                    sectionIndex,
                    criteriaIndex,
                    questionIndex,
                    question: criteria.questions[questionIndex],
                };
            }
        }
    }

    return null;
};

const toPersistedCoachInsights = (feedback: CoachFeedback, answerText: string): QuestionCoachInsights => ({
    ...feedback,
    answerText,
});

/** Persist Mia Coach insights onto the question document so they survive reloads. */
export const persistCoachInsights = createAsyncThunk(
    "questionnaire/persistCoachInsights",
    async (
        payload: { questionId: string; feedback: CoachFeedback; answerText: string },
        thunkApi
    ) => {
        const state = thunkApi.getState() as RootState;
        const found = findQuestionInState(state.questionnaire, payload.questionId);
        if (!found) {
            return thunkApi.rejectWithValue("Question not found");
        }

        if (!state.questionnaire.rolePermission?.editQuestionnaireContent) {
            return {
                questionId: payload.questionId,
                coachInsights: toPersistedCoachInsights(payload.feedback, payload.answerText),
            };
        }

        const coachInsights = toPersistedCoachInsights(payload.feedback, payload.answerText);
        const savedText = found.question.draftAnswer?.trim()
            ? found.question.draftAnswer
            : (found.question.answer ?? "");

        // Coach runs on debounce while the textarea value is still local-only
        // (saved on blur). Persisting the last-saved question here would write
        // a stale answer, unlock the field, and clobber in-progress typing.
        // Blur (or a later coach pass after save) persists insights instead.
        if (savedText !== payload.answerText) {
            return { questionId: payload.questionId, coachInsights };
        }

        await thunkApi
            .dispatch(
                updateQuestionnaireQuestion({
                    ...found.question,
                    coachInsights,
                })
            )
            .unwrap();

        return { questionId: payload.questionId, coachInsights };
    }
);

export interface AcceptAiAnswerPayload {
    question: IQuestionnaireQuestion;
    answer: string;
}

export const acceptAiAnswer = createAsyncThunk(
    "questionnaire/acceptAiAnswer",
    async (payload: AcceptAiAnswerPayload, thunkApi) => {
        const state = thunkApi.getState() as RootState;

        if (!state.questionnaire.rolePermission?.editQuestionnaireContent) {
            return thunkApi.rejectWithValue("You do not have permission to edit questionnaire answers.");
        }

        const { question, answer } = payload;
        const { aiAnsweredQuestionIds, aiEvidenceSources, coachFeedbackByQuestionId, coachedAnswerTextByQuestionId } =
            state.questionnaire;

        const hasDraft =
            aiAnsweredQuestionIds.includes(question.id) || !!question.draftAnswer?.trim();
        if (!hasDraft) {
            return thunkApi.rejectWithValue("This question has no AI-generated draft to accept.");
        }

        const coachFeedback = coachFeedbackByQuestionId[question.id];
        const coachInsights = coachFeedback
            ? toPersistedCoachInsights(
                  coachFeedback,
                  coachedAnswerTextByQuestionId[question.id] ?? answer
              )
            : question.coachInsights;

        const evidenceSources =
            aiEvidenceSources.length > 0
                ? aiEvidenceSources
                : question.draftEvidenceSources?.length
                    ? question.draftEvidenceSources
                    : question.evidenceSources;

        await thunkApi
            .dispatch(
                updateQuestionnaireQuestion({
                    ...question,
                    answer,
                    draftAnswer: "",
                    evidenceSources,
                    draftEvidenceSources: [],
                    coachInsights,
                })
            )
            .unwrap();

        return { questionId: question.id };
    }
);

export interface AcceptAllAiAnswersPayload {
    questions: IQuestionnaireQuestion[];
    previewAnswers: Record<string, string>;
}

export interface AcceptAllAiAnswersResult {
    succeededIds: string[];
    failedIds: string[];
}

export const acceptAllAiAnswers = createAsyncThunk(
    "questionnaire/acceptAllAiAnswers",
    async (payload: AcceptAllAiAnswersPayload, thunkApi) => {
        const state = thunkApi.getState() as RootState;

        if (!state.questionnaire.rolePermission?.editQuestionnaireContent) {
            return thunkApi.rejectWithValue("You do not have permission to edit questionnaire answers.");
        }

        const { questions, previewAnswers } = payload;
        const {
            aiAnsweredQuestionIds,
            aiEvidenceSources,
            coachFeedbackByQuestionId,
            coachedAnswerTextByQuestionId,
        } = state.questionnaire;

        if (aiAnsweredQuestionIds.length === 0) {
            const anyPersistedDrafts = questions.some((q) => !!q.draftAnswer?.trim());
            if (!anyPersistedDrafts) {
                return thunkApi.rejectWithValue("No AI-generated answers to accept.");
            }
        }

        const questionsToAccept = questions.filter(
            (q) => aiAnsweredQuestionIds.includes(q.id) || !!q.draftAnswer?.trim()
        );

        const results = await Promise.allSettled(
            questionsToAccept.map((question) => {
                const answer =
                    previewAnswers[question.id] ?? question.draftAnswer ?? question.answer;
                const coachFeedback = coachFeedbackByQuestionId[question.id];
                const coachInsights = coachFeedback
                    ? toPersistedCoachInsights(
                          coachFeedback,
                          coachedAnswerTextByQuestionId[question.id] ?? answer
                      )
                    : question.coachInsights;

                const evidenceSources =
                    aiEvidenceSources.length > 0
                        ? aiEvidenceSources
                        : question.draftEvidenceSources?.length
                            ? question.draftEvidenceSources
                            : question.evidenceSources;

                return thunkApi
                    .dispatch(
                        updateQuestionnaireQuestion({
                            ...question,
                            answer,
                            draftAnswer: "",
                            evidenceSources,
                            draftEvidenceSources: [],
                            coachInsights,
                        })
                    )
                    .unwrap();
            })
        );

        const succeededIds: string[] = [];
        const failedIds: string[] = [];

        results.forEach((result, index) => {
            const questionId = questionsToAccept[index].id;
            if (result.status === "fulfilled") {
                succeededIds.push(questionId);
            } else {
                failedIds.push(questionId);
            }
        });

        if (succeededIds.length === 0) {
            return thunkApi.rejectWithValue({
                message: "Failed to save answers. One or more questions may be locked by another user.",
                succeededIds,
                failedIds,
            });
        }

        return { succeededIds, failedIds } satisfies AcceptAllAiAnswersResult;
    }
);

export const generateAssessment = createAsyncThunk(
  "questionnaire/GenerateAssessment",
  async (
    payload: {
      token: string;
      clientId: string;
      /** Prefer UI/local answers (and AI drafts) over possibly stale Redux answers. */
      answerOverrides?: Record<string, string>;
    },
    thunkApi
  ) => {
    const { questionnaire } = thunkApi.getState() as RootState;

    if (!questionnaire.rolePermission?.generateAssessment) {
      return thunkApi.rejectWithValue(
        "You do not have permission to generate an assessment."
      );
    }

    const { activeSectionId, activeCriteriaId } = questionnaire;
    const areaId = questionnaire.questionnaire?.id;
    if (!areaId || !activeSectionId || !activeCriteriaId) {
      return thunkApi.rejectWithValue("Missing section or criteria context.");
    }

    const section = questionnaire.questionnaire?.questionnaireSections?.find(
      (s) => s.id === activeSectionId
    );
    const criteria = section?.criteria?.find((c) => c.id === activeCriteriaId);
    if (!criteria) {
      return thunkApi.rejectWithValue("Criteria not found.");
    }

    const fields = fieldsInExportOrder(criteria.fields || []).filter(
      (f) => f.type === FieldType.Text
    );
    const questions = (criteria.questions || []).map((q) => {
      const override = payload.answerOverrides?.[q.id];
      return {
        question: q.question || "",
        answer: (override !== undefined ? override : q.answer) || "",
      };
    });
    const availableTags = (criteria.tags || []).map(String);
    const hasAnswerContent = questions.some((q) => q.answer.trim().length > 0);
    if (!hasAnswerContent) {
      return thunkApi.rejectWithValue(
        "Add or accept questionnaire answers before generating a summary."
      );
    }

    const prompt = buildSummariseAssessmentPrompt({
      criteriaGuidance: criteria.fullCriteria || "not provided",
      questions,
      fields,
      availableTags,
    });

    // Tiles come from the server's access-checked "resources" event, the same source Analyse
    // uses. The fence in the answer text is not trusted; the agent can name files it was shown.
    let streamedResources: CurateResource[] = [];
    const streamSummarise = async (message: string) => {
      let answerLength = 0;
      const thisCallResources: CurateResource[] = [];
      const response = await sendMcpMessageStreaming(
        payload.token,
        message,
        payload.clientId,
        (event) => {
          if (event.type === "delta") {
            answerLength += event.text?.length ?? 0;
          }
          if (event.type === "resources") {
            thisCallResources.push(...normalizeCurateResources(event.items));
          }
          const progressText = streamEventToProgressText(event, answerLength);
          if (progressText) {
            thunkApi.dispatch(pushGenerateAnswersProgressStep(progressText));
          }
        },
        activeCriteriaId,
        activeCriteriaId,
        undefined,
        { mode: "Summarise" }
      );
      if (thisCallResources.length > 0) {
        streamedResources = thisCallResources;
      }
      return response;
    };

    let response = await streamSummarise(prompt);
    let parsed = parseSummariseAssessmentAnswer(response.answer, fields);
    if (!parsed) {
      response = await streamSummarise(
        "Return only the JSON object for the assessment, then the curate-resources fence if you already selected tiles. No commentary."
      );
      parsed = parseSummariseAssessmentAnswer(response.answer, fields);
    }
    if (!parsed) {
      return thunkApi.rejectWithValue("Could not parse the generated assessment.");
    }

    const hasFieldContent = parsed.fields.some((f) => (f.content || "").trim().length > 0);
    if (!hasFieldContent) {
      return thunkApi.rejectWithValue(
        "The generated assessment did not include Summary content. Please try Generate Again."
      );
    }

    const resources = streamedResources;

    await axiosInstance.post(
      `SaveGeneratedAssessment/${activeSectionId}/${activeCriteriaId}/${areaId}`,
      {
        fields: parsed.fields.map((f) => ({ id: f.id, content: f.content || "" })),
        tags: parsed.tags,
        grade: parsed.grade,
        recommendedCurateResources: resources,
      }
    );

    return { ...parsed, resources } as SummariseResponse & { resources: CurateResource[] };
  }
);


export const updateQuestionnaireGrade = createAsyncThunk("questionnaire/UpdateQuestionnaireGrade", async (grade: IGrade, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const questionnaireId = state.questionnaire.questionnaire?.id;
    if(questionnaireId){
        const response = await axiosInstance.patch(`UpdateQuestionnaireGrade/${questionnaireId}/${grade}`);
    }
    return grade as IGrade;
})

export const updateQuestionnaireTitle = createAsyncThunk("questionnaire/UpdateQuestionnaireTitle", async (title: string, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const questionnaireId = state.questionnaire.questionnaire?.id;


    if(!state.questionnaire.rolePermission?.editTitle)
        return thunkApi.rejectWithValue("no permission to edit title");

    scheduleDebouncedQuestionnaireSave(async () => {
      if (questionnaireId) {
        await axiosInstance.patch(`UpdateQuestionnaireTitle/${questionnaireId}`, title);
      }
    });

    return title as IGrade;
})


export const updateQuestionnaireSectionStatus = createAsyncThunk("questionnaire/UpdateQuestionnaireSectionStatus", async (payload: UpdateQuestionnaireSectionStatusPayload, thunkApi) => {
    const response = await axiosInstance.patch(`UpdateQuestionnaireSectionStatus/${payload.sectionId}/${payload.status}`);
    return payload as UpdateQuestionnaireSectionStatusPayload;
});

export const updateNotesPermissions = createAsyncThunk(
    "questionnaire/UpdateNotesPermissions",
    async ({ areaId, request }: { areaId: string; request: UpdateNotesPermissionsRequest }, thunkApi) => {
        try {
            const response = await UpdateNotesPermissions(areaId, request);
            return { 
                notesPermissions: response.notesPermissions,
                notesDefaultPermission: response.notesDefaultPermission
            };
        } catch (error) {
            return thunkApi.rejectWithValue(error);
        }
    }
);

export const updateRestrictClientAdminQuestionnaireAccess = createAsyncThunk(
    "questionnaire/updateRestrictClientAdminQuestionnaireAccess",
    async (
        { areaId, restrictClientAdminQuestionnaireAccess }: { areaId: string; restrictClientAdminQuestionnaireAccess: boolean },
        thunkApi
    ) => {
        try {
            const data = await patchRestrictClientAdminQuestionnaireAccess(areaId, {
                restrictClientAdminQuestionnaireAccess,
            });
            return {
                restrictClientAdminQuestionnaireAccess: data.restrictClientAdminQuestionnaireAccess,
                restrictClientAdminQuestionnaireLockedBySub: data.restrictClientAdminQuestionnaireLockedBySub ?? null,
            };
        } catch (error) {
            return thunkApi.rejectWithValue(error);
        }
    }
);

// result type: either a newer value to apply, a conflict flag, or null (no change)
export type CheckFieldChangedResult = { 
  fieldId: string; 
  serverValue: string;
  conflict: boolean;
};

export const CheckFieldChanged = createAsyncThunk<
  CheckFieldChangedResult,
  string,
  { state: RootState }
>("questionnaire/CheckFieldChanged", async (fieldId, thunkApi) => {
  const state = thunkApi.getState();
  const { questionnaire, activeCriteriaId } = state.questionnaire;

  const field =
    state.questionnaire.questionnaire?.questionnaireSections
      .flatMap(s => s.criteria)
      .flatMap(c => c.fields)
      .find(f => f.id === fieldId);

  if (!field) {
    throw new Error("Field not found");
  }

  const specialField = specialFieldToRouteValue(field.specialField);
  const res = await axiosInstance.put<string>(
    `CheckDiagnosticFieldChanged/${activeCriteriaId}/${specialField}/${questionnaire?.id}`,
    field.content ?? "",
    { validateStatus: () => true }
  );

  if (res.status === 409) {
    toast.error("Another user is currently editing this field. Please try again later.");
    return {
      fieldId,
      serverValue: field.content ?? "",
      conflict: true,
    };
  }

  if (res.status !== 200 || typeof res.data !== "string") {
    return { fieldId, serverValue: field.content ?? "", conflict: false };
  }

  const serverValue = res.data;
  if (serverValue !== (field.content ?? "")) {
    toast.warn("Changes were made to this field by another user");
  }

  return { fieldId, serverValue, conflict: false };
});

function specialFieldToRouteValue(specialField: unknown): number {
  if (typeof specialField === "number" && Number.isFinite(specialField)) {
    return specialField;
  }
  if (typeof specialField === "string") {
    const asNumber = Number(specialField);
    if (!Number.isNaN(asNumber)) {
      return asNumber;
    }
    const fromName = SpecialField[specialField as keyof typeof SpecialField];
    if (typeof fromName === "number") {
      return fromName;
    }
  }
  if (specialField && typeof specialField === "object") {
    for (const key of Object.keys(specialField as object)) {
      const fromName = SpecialField[key as keyof typeof SpecialField];
      if (typeof fromName === "number") {
        return fromName;
      }
    }
  }
  return SpecialField.None;
}

export const UpdateFieldContent = createAsyncThunk("questionnaire/UpdateFieldContent", async (payload: UpdateFieldContentPayload, thunkApi) => {

    const state = thunkApi.getState() as RootState;
    const productType = state.questionnaire.questionnaire?.productType;

    if (payload.field) {
        const response = await UpdateEvaluateFieldByCriteriaIdAndActivityId(payload.field, payload.criteriaId, payload.activityId);

        if (response?.status === 409) {
            toast.warn("Changes were made to this field by another user");
        }

        const contextId = productType === ProductType.Evaluate ? payload.activityId + payload.criteriaId : payload.activityId;
        await axiosInstance.get(`UnlockResource/${contextId}`);
    }
    
    return payload as UpdateFieldContentPayload;
});

export const UnlockField = createAsyncThunk("questionnaire/UnlockField", async (payload: UnlockFieldPayload, thunkApi) => {

    const state = thunkApi.getState() as RootState;
    const productType = state.questionnaire.questionnaire?.productType;

    const contextId = productType === ProductType.Evaluate ? payload.activityId + payload.criteriaId : payload.activityId;

    await axiosInstance.get(`UnlockResource/${contextId}`);

    return payload as UnlockFieldPayload;
});

export const QuestionnaireSlice = createSlice({
    name: "Questionnaire",
    initialState,
    reducers:{
        setQuestionnaire: (state, action: PayloadAction<IProductQuestionnaire>) => {  
            state.questionnaire = action.payload;
            applyActiveCriteriaResources(state);
        },
        clearQuestionnaire: (state) => {
            state.questionnaire = undefined;
            state.areaKpiSummary = undefined;
            state.questionnaireLoadingAreaId = undefined;
            state.comments = [];
            state.commentsByCriteriaId = {};
            state.linkedEvidenceByCriteriaId = {};
            state.conversationFilesByCriteriaId = {};
            state.aiAnswerSnapshot = null;
            state.aiGeneratedAnswers = null;
            state.aiAnsweredQuestionIds = [];
            state.aiSkipReasonsByQuestionId = {};
            state.aiEvidenceSources = [];
            state.isGeneratingAnswersFromEvidence = false;
            state.generateAnswersProgressSteps = [];
            state.isAcceptingAiAnswers = false;
            state.coachFeedbackByQuestionId = {};
            state.coachStatusByQuestionId = {};
            state.coachProgressByQuestionId = {};
            state.coachConversationId = null;
            state.coachedAnswerTextByQuestionId = {};
            state.recommendedCurateResources = [];
        },
        setQuestionnaireSection:(state, action: PayloadAction<{section: IQuestionnaireSection}>) => {
            const {section} = action.payload;
            const index = getSectionIndexById(state.questionnaire!.questionnaireSections, section.id);
            state.questionnaire!.questionnaireSections[index] = section;
        },
        setActiveQuestionnaireSection:(state, action: PayloadAction<{sectionId: string}>) => {
            state.activeSectionId = action.payload.sectionId;
        },
        setActiveCriteria:(state, action: PayloadAction<{itemId: string}>) => {
            state.activeCriteriaId = action.payload.itemId;
            applyActiveCriteriaComments(state, action.payload.itemId);
            applyActiveCriteriaResources(state);
            state.aiAnswerSnapshot = null;
            state.aiGeneratedAnswers = null;
            state.aiAnsweredQuestionIds = [];
            state.aiEvidenceSources = [];
            state.isGeneratingAnswersFromEvidence = false;
            state.generateAnswersProgressSteps = [];
            state.isAcceptingAiAnswers = false;
            state.coachFeedbackByQuestionId = {};
            state.coachStatusByQuestionId = {};
            state.coachProgressByQuestionId = {};
            state.coachConversationId = null;
            state.coachedAnswerTextByQuestionId = {};
        },
        clearComments: (state) => {
            state.comments = [];
            state.commentsLoading = false;
        },
        updateCriteriaContextData: (
            state,
            action: PayloadAction<{
                criteriaId: string;
                linkedEvidence?: PrefetchedLinkedEvidenceFile[];
                conversationFiles?: PrefetchedConversationFile[];
                comments?: Comment[];
            }>
        ) => {
            const { criteriaId, linkedEvidence, conversationFiles, comments } = action.payload;
            if (linkedEvidence !== undefined) {
                state.linkedEvidenceByCriteriaId[criteriaId] = linkedEvidence;
            }
            if (conversationFiles !== undefined) {
                state.conversationFilesByCriteriaId[criteriaId] = conversationFiles;
            }
            if (comments !== undefined) {
                state.commentsByCriteriaId[criteriaId] = comments;
                if (state.activeCriteriaId === criteriaId) {
                    state.comments = comments;
                }
            }
        },
        lockField:(state, action: PayloadAction<{fieldId: string, locked: boolean}>) => {
            const {fieldId, locked} = action.payload;
            const section = state.questionnaire?.questionnaireSections.find(s => s.id === state.activeSectionId);
            const criteria = section?.criteria.find(c => c.id === state.activeCriteriaId);
            const field = criteria?.fields.find(f => f.id === fieldId);
            if (field) {
                field.locked = locked;
            }
        },
        suspendCriteriaFields:(state, action: PayloadAction<{criteriaId: string, locked: boolean}>) => {
            const {criteriaId, locked} = action.payload;
            const section = state.questionnaire?.questionnaireSections.find(s => s.id === state.activeSectionId);
            console.log(section)
            const criteria = section?.criteria.find(c => c.id === criteriaId);
            console.log(criteria)
            if (criteria) {
                criteria.fields.forEach(f => f.suspended = locked);
                console.log(criteria.fields)
            }
        },
        setIsViewingResults:(state, action: PayloadAction<boolean>) => {
            state.isViewingResults = action.payload;
        },
        lockQuestion:(state, action: PayloadAction<{questionId: string, locked: boolean}>) => {
            const {questionId, locked} = action.payload;
            const section = state.questionnaire?.questionnaireSections.find(s => s.id === state.activeSectionId);
            const criteria = section?.criteria.find(c => c.id === state.activeCriteriaId);
            const question = criteria?.questions.find(q => q.id === questionId);
            if (question) {
                question.locked = locked;
            }
        },
        clearAiAnswerPreview: (state) => {
            state.aiAnswerSnapshot = null;
            state.aiGeneratedAnswers = null;
            state.aiAnsweredQuestionIds = [];
            state.aiEvidenceSources = [];
            state.isGeneratingAnswersFromEvidence = false;
            state.generateAnswersProgressSteps = [];
            state.isAcceptingAiAnswers = false;
            state.coachFeedbackByQuestionId = {};
            state.coachStatusByQuestionId = {};
            state.coachProgressByQuestionId = {};
            state.coachConversationId = null;
            state.coachedAnswerTextByQuestionId = {};
        },
        clearCoachFeedbackForQuestion: (state, action: PayloadAction<string>) => {
            const questionId = action.payload;
            delete state.coachFeedbackByQuestionId[questionId];
            delete state.coachStatusByQuestionId[questionId];
            delete state.coachProgressByQuestionId[questionId];
            delete state.coachedAnswerTextByQuestionId[questionId];
        },
        setCoachProgress: (state, action: PayloadAction<{ questionId: string; text: string }>) => {
            const { questionId, text } = action.payload;
            state.coachProgressByQuestionId[questionId] = text;
        },
        pushGenerateAnswersProgressStep: (state, action: PayloadAction<string>) => {
            appendProgressStep(state.generateAnswersProgressSteps, action.payload);
        },
        revertAiAnswer: (state, action: PayloadAction<string>) => {
            const questionId = action.payload;
            if (!state.aiAnswerSnapshot || !state.aiAnsweredQuestionIds.includes(questionId)) {
                return;
            }

            delete state.coachFeedbackByQuestionId[questionId];
            delete state.coachStatusByQuestionId[questionId];
            delete state.coachProgressByQuestionId[questionId];
            delete state.coachedAnswerTextByQuestionId[questionId];

            const found = findQuestionInState(state, questionId);
            if (found) {
                state.questionnaire!.questionnaireSections[found.sectionIndex].criteria[
                    found.criteriaIndex
                ].questions[found.questionIndex] = {
                    ...found.question,
                    draftAnswer: "",
                    draftEvidenceSources: [],
                    coachInsights: undefined,
                };
            }

            state.aiAnsweredQuestionIds = state.aiAnsweredQuestionIds.filter((id) => id !== questionId);

            if (state.aiGeneratedAnswers) {
                delete state.aiGeneratedAnswers[questionId];
            }

            if (state.aiAnsweredQuestionIds.length === 0) {
                state.aiAnswerSnapshot = null;
                state.aiGeneratedAnswers = null;
                state.aiEvidenceSources = [];
            }
        },
        revertAllAiAnswers: (state) => {
            if (!state.aiAnswerSnapshot || state.aiAnsweredQuestionIds.length === 0) {
                return;
            }

            state.aiAnsweredQuestionIds.forEach((questionId) => {
                delete state.coachFeedbackByQuestionId[questionId];
                delete state.coachStatusByQuestionId[questionId];
                delete state.coachProgressByQuestionId[questionId];
                delete state.coachedAnswerTextByQuestionId[questionId];

                const found = findQuestionInState(state, questionId);
                if (found) {
                    state.questionnaire!.questionnaireSections[found.sectionIndex].criteria[
                        found.criteriaIndex
                    ].questions[found.questionIndex] = {
                        ...found.question,
                        draftAnswer: "",
                        draftEvidenceSources: [],
                        coachInsights: undefined,
                    };
                }
            });

            state.aiAnswerSnapshot = null;
            state.aiGeneratedAnswers = null;
            state.aiAnsweredQuestionIds = [];
            state.aiEvidenceSources = [];
        },
        /** Restore coach UI state from insights persisted on question documents. */
        hydrateCoachFeedbackFromQuestions: (state, action: PayloadAction<IQuestionnaireQuestion[]>) => {
            action.payload.forEach((question) => {
                const insights = question.coachInsights;
                if (!insights || insights.quality === "empty") {
                    return;
                }

                // Don't overwrite fresher in-memory feedback for this question.
                if (state.coachFeedbackByQuestionId[question.id]) {
                    return;
                }

                const { answerText, ...feedback } = insights;
                state.coachFeedbackByQuestionId[question.id] = feedback;
                state.coachStatusByQuestionId[question.id] = "done";
                state.coachedAnswerTextByQuestionId[question.id] =
                    answerText ?? question.draftAnswer ?? question.answer ?? "";
            });
        },
        /** Restore AI draft preview state from draftAnswer persisted on questions. */
        hydrateAiDraftsFromQuestions: (state, action: PayloadAction<IQuestionnaireQuestion[]>) => {
            const snapshot: Record<string, string> = { ...(state.aiAnswerSnapshot ?? {}) };
            const drafts: Record<string, string> = { ...(state.aiGeneratedAnswers ?? {}) };
            const answeredIds = new Set(state.aiAnsweredQuestionIds);
            let evidenceSources = state.aiEvidenceSources;
            let addedAny = false;

            action.payload.forEach((question) => {
                const draft = question.draftAnswer?.trim();
                if (!draft) {
                    return;
                }

                // Don't overwrite an active in-memory draft session for this question.
                if (answeredIds.has(question.id) && drafts[question.id] !== undefined) {
                    return;
                }

                drafts[question.id] = question.draftAnswer!;
                snapshot[question.id] = question.answer ?? "";
                answeredIds.add(question.id);
                addedAny = true;

                if (
                    evidenceSources.length === 0 &&
                    question.draftEvidenceSources &&
                    question.draftEvidenceSources.length > 0
                ) {
                    evidenceSources = question.draftEvidenceSources;
                }
            });

            if (!addedAny) {
                return;
            }

            state.aiAnswerSnapshot = snapshot;
            state.aiGeneratedAnswers = drafts;
            state.aiAnsweredQuestionIds = Array.from(answeredIds);
            state.aiEvidenceSources = evidenceSources;
        },
    },
    extraReducers: (builder) => {

    builder.addCase(CheckFieldChanged.fulfilled, (state, action) => {
        const { fieldId, serverValue, conflict } = action.payload;

        const field =
            state.questionnaire?.questionnaireSections
            .flatMap(s => s.criteria)
            .flatMap(c => c.fields)
            .find(f => f.id === fieldId);

        if (!field) return;

        field.suspended = conflict;

        if (conflict) {
            return;
        }

        // only apply if different
        if (serverValue !== (field.content ?? "")) {
            field.content = serverValue;
        }
    });

        builder.addCase(UpdateCriteriaDate.pending, (state, action) => {
  const { criteriaId, sectionId, date } = action.meta.arg as UpdateCriteriaDateRequest;
  const section = state.questionnaire?.questionnaireSections.find(s => s.id === sectionId);
  const criteria = section?.criteria.find(c => c.id === criteriaId);
  if (criteria && section) {
    criteria.date = date;                        // optimistic write
    section.completionStatus = computeSectionStatus(section);  // recompute
  }
});

        builder.addCase(UpdateAreaDueDate.pending, (state, action) => {
            state.areaDueDate = action.meta.arg.dueDate; // optimistic write
        });

        builder.addCase(UpdateAreaDueDate.rejected, (state, action) => {
            toast.error(String(action.payload ?? "Failed to update the due date"));
        });

        builder.addCase(updateQuestionnaireScope.fulfilled, (state, action) => {
            if (!state.questionnaire) return;
            state.questionnaire.scope = action.payload as string;
            });
builder.addCase(UpdateCriteriaDate.fulfilled, (state, action) => {
  const { criteriaId, sectionId, date } = action.payload as UpdateCriteriaDateRequest;
  const section = state.questionnaire?.questionnaireSections.find(s => s.id === sectionId);
  const criteria = section?.criteria.find(c => c.id === criteriaId);
  if (criteria && section) {
    criteria.date = date;
    section.completionStatus = computeSectionStatus(section);
  }
});

        builder.addCase(UpdateCriteriaGrade.fulfilled, (state, action) => {
            const {gradeId, criteriaId} = action.payload;
            const section = state.questionnaire?.questionnaireSections.find(s => s.criteria.some(c => c.id === criteriaId));
            const criteria = section?.criteria.find(c => c.id === criteriaId);
            if (criteria && section) {
                criteria.grade = gradeId as IGrade;
            }
        });

        builder.addCase(UpdateCriteriaGrade.rejected, (state, action) => {
            // Handle validation errors as needed
        });

        builder.addCase(UpdateCriteriaLowerField.fulfilled, (state, action) => {
            const { criteriaId, updateType, data, listData } = action.payload;
            const section = state.questionnaire?.questionnaireSections?.find(s => s.criteria.some(c => c.id === criteriaId));
            const criteria = section?.criteria.find(c => c.id === criteriaId);
            if (!criteria) return;
            switch (updateType) {
                case UpdateLowerFieldType.UpdatePerson:
                    if (listData !== undefined) criteria.person = listData;
                    break;
                case UpdateLowerFieldType.UpdateDropdown:
                    if (data !== undefined) criteria.dropdown = data;
                    break;
                case UpdateLowerFieldType.UpdatePicklist:
                    if (listData !== undefined) criteria.picklist = listData.map(v => ({ value: v, label: v, required: false }));
                    break;
                case UpdateLowerFieldType.UpdateTags:
                    if (listData !== undefined) criteria.tags = listData;
                    break;
                default:
                    break;
            }
        });

        builder.addCase(getQuestionnaireByAreaId.pending, (state, action) => {
            state.questionnaireLoadingAreaId = resolveGetQuestionnaireByAreaIdArg(action.meta.arg).areaId;
        });

        builder.addCase(getQuestionnaireByAreaId.fulfilled, (state, action) => {
            state.questionnaireLoadingAreaId = undefined;
            state.questionnaire = action.payload.questionnaire;
            state.areaKpiSummary = action.payload.kpiSummary;
            state.areaDueDate = isSetDate(action.payload.dueDate) ? toDate(action.payload.dueDate!) : undefined;
            state.rolePermission = action.payload.rolePermission;
            state.commentsByCriteriaId = normalizeCommentsByCriteriaId(
                action.payload.commentsByCriteriaId as Record<string, unknown> | undefined
            );
            state.linkedEvidenceByCriteriaId = normalizeLinkedEvidenceByCriteriaId(
                action.payload.linkedEvidenceByCriteriaId as Record<string, unknown> | undefined
            );
            state.conversationFilesByCriteriaId = normalizeConversationFilesByCriteriaId(
                action.payload.conversationFilesByCriteriaId as Record<string, unknown> | undefined
            );

            if (state.activeCriteriaId) {
                applyActiveCriteriaComments(state, state.activeCriteriaId);
            }
            applyActiveCriteriaResources(state);

            if (state.questionnaire) normalizeDates(state.questionnaire);
        });

        builder.addCase(getQuestionnaireByAreaId.rejected, (state) => {
            state.questionnaireLoadingAreaId = undefined;
        });
        
        builder.addCase(updateQuestionnaireQuestion.fulfilled, (state, action) => {

            if (!state.questionnaire) {
                return;
            }

            const stateQuestion = action.payload as IQuestionnaireQuestion;
            const sections = state.questionnaire.questionnaireSections as IQuestionnaireSection[];

            const sectionIndex = getSectionIndex(sections, stateQuestion);
            if(sectionIndex === -1){
                return;
            }
            const section = sections[sectionIndex];
            const criteriaIndex = getCriteriaIndex(section, stateQuestion);
            if(criteriaIndex === -1){
                return;
            }
            const criteria = section.criteria[criteriaIndex];
            const questionIndex = getQuestionIndex(criteria, stateQuestion);
            
            state.questionnaire.questionnaireSections[sectionIndex].criteria[criteriaIndex].questions[questionIndex] = action.payload;
        });

        builder.addCase(updateQuestionnaireGrade.fulfilled, (state, action) => {

            if (!state.questionnaire) {
                return;
            }

            const grade = action.payload as IGrade;
            
            state.questionnaire.overallGrade = grade;
        });

        builder.addCase(updateQuestionnaireTitle.fulfilled, (state, action) => {

            if (!state.questionnaire) {
                return;
            }

            const title = action.payload as string;
            
            state.questionnaire.title = title;
        });

        builder.addCase(updateNotesPermissions.fulfilled, (state, action) => {
            if (!state.questionnaire) {
                return;
            }
            if (action.payload.notesPermissions !== undefined) {
                state.questionnaire.notesPermissions = action.payload.notesPermissions;
            }
            if (action.payload.notesDefaultPermission !== undefined) {
                state.questionnaire.notesDefaultPermission = action.payload.notesDefaultPermission;
            }
        });

        builder.addCase(updateRestrictClientAdminQuestionnaireAccess.fulfilled, (state, action) => {
            if (!state.questionnaire) return;
            state.questionnaire.restrictClientAdminQuestionnaireAccess = action.payload.restrictClientAdminQuestionnaireAccess;
            state.questionnaire.restrictClientAdminQuestionnaireLockedBySub = action.payload.restrictClientAdminQuestionnaireLockedBySub;
        });

        builder.addCase(updateQuestionnaireSectionStatus.fulfilled, (state, action) => {

            if (!state.questionnaire) {
                return;
            }

            const payload = action.payload as UpdateQuestionnaireSectionStatusPayload;
            const sections = state.questionnaire.questionnaireSections as IQuestionnaireSection[];

            const sectionIndex = getSectionIndexById(sections, payload.sectionId);

            state.questionnaire.questionnaireSections[sectionIndex].completionStatus = payload.status;
        });

        builder.addCase(generateAssessment.fulfilled, (state, action) => {

            if (!state.questionnaire) {
                return;
            }

            const response = action.payload as SummariseResponse & { resources?: CurateResource[] };
            const sections = state.questionnaire.questionnaireSections as IQuestionnaireSection[];

            const sectionIndex = getSectionIndexById(sections, state.activeSectionId);
            const section = sections[sectionIndex];
            const criteriaIndex = getCriteriaIndexById(section, state.activeCriteriaId);

            const existingFields = state.questionnaire.questionnaireSections[sectionIndex].criteria[criteriaIndex].fields;

            const updatedFields = existingFields.map(field => {  
                const matchingNewField = response.fields.find(newField => newField.id === field.id);  
                return matchingNewField ? matchingNewField : field;  
            });  

            //state.questionnaire.questionnaireSections[sectionIndex].criteria[criteriaIndex].grade = response.grade;
            state.questionnaire.questionnaireSections[sectionIndex].criteria[criteriaIndex].generatedResults = true;
            state.questionnaire.questionnaireSections[sectionIndex].criteria[criteriaIndex].fields = updatedFields;
            state.questionnaire.questionnaireSections[sectionIndex].criteria[criteriaIndex].tags = response.tags;
            state.questionnaire.questionnaireSections[sectionIndex].criteria[criteriaIndex].summaryEvidenceSources =
                response.summaryEvidenceSources ?? [];
            const resources = response.resources ?? [];
            state.questionnaire.questionnaireSections[sectionIndex].criteria[criteriaIndex]
                .recommendedCurateResources = resources;
            state.recommendedCurateResources = resources;
            state.generateAnswersProgressSteps = [];
        });

        builder.addCase(generateAssessment.pending, (state) => {
            state.generateAnswersProgressSteps = ["Preparing assessment..."];
            state.recommendedCurateResources = [];
            const sections = state.questionnaire?.questionnaireSections;
            if (!sections) {
                return;
            }
            const sectionIndex = getSectionIndexById(sections, state.activeSectionId);
            if (sectionIndex < 0) {
                return;
            }
            const criteriaIndex = getCriteriaIndexById(sections[sectionIndex], state.activeCriteriaId);
            if (criteriaIndex < 0) {
                return;
            }
            state.questionnaire!.questionnaireSections[sectionIndex].criteria[criteriaIndex]
                .recommendedCurateResources = [];
        });

        builder.addCase(generateAssessment.rejected, (state) => {
            state.generateAnswersProgressSteps = [];
        });

        builder.addCase(generateAnswersFromEvidence.pending, (state, action) => {
            state.isGeneratingAnswersFromEvidence = true;
            state.generateAnswersProgressSteps = ["Preparing..."];
            action.meta.arg.questions.forEach((question) => {
                delete state.aiSkipReasonsByQuestionId[question.id];
            });
        });
        builder.addCase(generateAnswersFromEvidence.fulfilled, (state, action) => {
            state.isGeneratingAnswersFromEvidence = false;
            state.generateAnswersProgressSteps = [];
            state.aiAnswerSnapshot = action.payload.aiAnswerSnapshot;
            state.aiGeneratedAnswers = action.payload.aiGeneratedAnswers;
            state.aiAnsweredQuestionIds = action.payload.aiAnsweredQuestionIds;
            state.aiEvidenceSources = action.payload.aiEvidenceSources;

            action.payload.aiAnsweredQuestionIds.forEach((questionId) => {
                delete state.aiSkipReasonsByQuestionId[questionId];
            });
            Object.assign(state.aiSkipReasonsByQuestionId, action.payload.skipReasonsByQuestionId);

            // Stamp draft + draft evidence onto questions in Redux. Accepted
            // evidenceSources stay unchanged until the user accepts the draft.
            const sourcesByQuestion = action.payload.sourcesByQuestionId ?? {};
            action.payload.aiAnsweredQuestionIds.forEach((questionId) => {
                const found = findQuestionInState(state, questionId);
                if (!found) {
                    return;
                }
                const draftAnswer = action.payload.aiGeneratedAnswers[questionId];
                const sources = sourcesByQuestion[questionId] ?? [];
                state.questionnaire!.questionnaireSections[found.sectionIndex].criteria[
                    found.criteriaIndex
                ].questions[found.questionIndex] = {
                    ...found.question,
                    draftAnswer,
                    draftEvidenceSources:
                        sources.length > 0 ? sources : found.question.draftEvidenceSources,
                };
            });
        });
        builder.addCase(generateAnswersFromEvidence.rejected, (state) => {
            state.isGeneratingAnswersFromEvidence = false;
            state.generateAnswersProgressSteps = [];
        });

        builder.addCase(fetchCoachFeedback.pending, (state, action) => {
            const { questionId } = action.meta.arg;
            state.coachStatusByQuestionId[questionId] = "loading";
            state.coachProgressByQuestionId[questionId] = "Reviewing your answer...";
        });
        builder.addCase(fetchCoachFeedback.fulfilled, (state, action) => {
            const { questionId, answerText, feedback, conversationId, requestSeq } = action.payload;

            // Ignore stale responses from superseded requests.
            if (coachRequestSeqByQuestionId[questionId] !== requestSeq) {
                return;
            }

            state.coachFeedbackByQuestionId[questionId] = feedback;
            state.coachStatusByQuestionId[questionId] = "done";
            state.coachedAnswerTextByQuestionId[questionId] = answerText;
            delete state.coachProgressByQuestionId[questionId];
            if (conversationId) {
                state.coachConversationId = conversationId;
            }

            // Keep insights on the question model so blur/accept saves retain them.
            const found = findQuestionInState(state, questionId);
            if (found) {
                state.questionnaire!.questionnaireSections[found.sectionIndex].criteria[
                    found.criteriaIndex
                ].questions[found.questionIndex] = {
                    ...found.question,
                    coachInsights: toPersistedCoachInsights(feedback, answerText),
                };
            }
        });
        builder.addCase(fetchCoachFeedback.rejected, (state, action) => {
            const { questionId } = action.meta.arg;
            const rejection = action.payload as { requestSeq?: number } | undefined;
            if (rejection?.requestSeq !== undefined && coachRequestSeqByQuestionId[questionId] !== rejection.requestSeq) {
                return;
            }
            // Coaching is advisory; record the error but never surface a blocking failure.
            state.coachStatusByQuestionId[questionId] = "error";
            delete state.coachProgressByQuestionId[questionId];
        });

        builder.addCase(acceptAiAnswer.pending, (state) => {
            state.isAcceptingAiAnswers = true;
        });
        builder.addCase(acceptAiAnswer.fulfilled, (state, action) => {
            state.isAcceptingAiAnswers = false;

            const { questionId } = action.payload;
            state.aiAnsweredQuestionIds = state.aiAnsweredQuestionIds.filter((id) => id !== questionId);

            if (state.aiAnsweredQuestionIds.length === 0) {
                state.aiAnswerSnapshot = null;
                state.aiGeneratedAnswers = null;
            } else if (state.aiGeneratedAnswers) {
                delete state.aiGeneratedAnswers[questionId];
            }
        });
        builder.addCase(acceptAiAnswer.rejected, (state) => {
            state.isAcceptingAiAnswers = false;
        });

        builder.addCase(acceptAllAiAnswers.pending, (state) => {
            state.isAcceptingAiAnswers = true;
        });
        builder.addCase(acceptAllAiAnswers.fulfilled, (state, action) => {
            state.isAcceptingAiAnswers = false;

            const { failedIds } = action.payload;

            if (failedIds.length === 0) {
                state.aiAnswerSnapshot = null;
                state.aiGeneratedAnswers = null;
                state.aiAnsweredQuestionIds = [];
            } else {
                state.aiAnsweredQuestionIds = failedIds;
                if (state.aiGeneratedAnswers) {
                    const remainingAnswers: Record<string, string> = {};
                    failedIds.forEach((id) => {
                        if (state.aiGeneratedAnswers![id] !== undefined) {
                            remainingAnswers[id] = state.aiGeneratedAnswers![id];
                        }
                    });
                    state.aiGeneratedAnswers = remainingAnswers;
                }
            }
        });
        builder.addCase(acceptAllAiAnswers.rejected, (state) => {
            state.isAcceptingAiAnswers = false;
        });

        builder.addCase(UpdateFieldContent.fulfilled, (state, action) => {
            const { field } = action.payload;
            if (!state.questionnaire) {
                return;
            }
            
            const sections = state.questionnaire.questionnaireSections as IQuestionnaireSection[];
            for (const section of sections) {
                for (const criteria of section.criteria) {
                    const existingField = criteria.fields.find(f => f.id === field.id);
                    if (existingField) {
                        existingField.content = field.content;
                        return;
                    }
                }
            }
        });

        // Comment management reducers
        builder.addCase(fetchCommentsByCriteriaId.pending, (state) => {
            state.commentsLoading = true;
        });
        builder.addCase(fetchCommentsByCriteriaId.fulfilled, (state, action) => {
            state.comments = action.payload;
            state.commentsLoading = false;
        });
        builder.addCase(fetchCommentsByCriteriaId.rejected, (state) => {
            state.commentsLoading = false;
        });

        builder.addCase(createComment.fulfilled, (state, action) => {
            const comment = normalizePrefetchedComment(action.payload as unknown as Record<string, unknown>);
            state.comments.push(comment);
            const criteriaId = state.activeCriteriaId;
            if (criteriaId) {
                const existing = state.commentsByCriteriaId[criteriaId] ?? [];
                state.commentsByCriteriaId[criteriaId] = [...existing, comment];
            }
        });

        builder.addCase(editComment.fulfilled, (state, action) => {
            const { commentId, text } = action.payload;
            const comment = state.comments.find(c => c.id === commentId);
            if (comment) {
                comment.CommentText = text;
            }
        });

        builder.addCase(deleteComment.fulfilled, (state, action) => {
            state.comments = state.comments.filter(comment => comment.id !== action.payload);
        });
    }
})




export const selectSectionById = (sectionId: string) => createSelector(
  [(state: RootState) => state.questionnaire?.questionnaire?.questionnaireSections],
  (questionnaireSections = []) => questionnaireSections.find(section => section.id === sectionId)
);

export const selectCriteriaById = (sectionId: string, criteriaId: string) => createSelector(
  [selectSectionById(sectionId)],
  (section) => section?.criteria.find(criteria => criteria.id === criteriaId)
);

export const selectFieldById = (fieldId: string) => createSelector(
  [(state: RootState) => state.questionnaire?.questionnaire?.questionnaireSections],
  (questionnaireSections = []) => {
    for (const section of questionnaireSections) {
      for (const criteria of section.criteria) {
        const field = criteria.fields.find(field => field.id === fieldId);
        if (field) {
          return field;
        }
      }
    }
    return undefined;
  }
);

export const selectQuestionById = (sectionId: string, criteriaId: string, questionId: string) => createSelector(
  [selectCriteriaById(sectionId, criteriaId)],
  (criteria) => criteria?.questions.find(question => question.id === questionId)
);

export const selectCriteriaCompletionStatus = (sectionId: string, criteriaId: string) =>
  createSelector([selectCriteriaById(sectionId, criteriaId)],
    (criteria) => !!criteria && isSetDate(criteria.date)
  );
  
export const selectRolePermissions = () => (state: RootState) => state.questionnaire.rolePermission;

export const selectAreaDueDate = (state: RootState) => state.questionnaire.areaDueDate;

export const selectIsGeneratingAnswersFromEvidence = (state: RootState) =>
    state.questionnaire.isGeneratingAnswersFromEvidence;

export const selectGenerateAnswersProgressSteps = (state: RootState) =>
    state.questionnaire.generateAnswersProgressSteps;

export const selectIsAcceptingAiAnswers = (state: RootState) =>
    state.questionnaire.isAcceptingAiAnswers;

export const selectAiAnswerSnapshot = (state: RootState) =>
    state.questionnaire.aiAnswerSnapshot;

export const selectAiGeneratedAnswers = (state: RootState) =>
    state.questionnaire.aiGeneratedAnswers;

export const selectAiAnsweredQuestionIds = (state: RootState) =>
    state.questionnaire.aiAnsweredQuestionIds;

export const selectAiSkipReasonsByQuestionId = (state: RootState) =>
    state.questionnaire.aiSkipReasonsByQuestionId;

export const selectAiEvidenceSources = (state: RootState) =>
    state.questionnaire.aiEvidenceSources;

export const selectCoachFeedbackByQuestionId = (state: RootState) =>
    state.questionnaire.coachFeedbackByQuestionId;

export const selectCoachStatusByQuestionId = (state: RootState) =>
    state.questionnaire.coachStatusByQuestionId;

export const selectCoachProgressByQuestionId = (state: RootState) =>
    state.questionnaire.coachProgressByQuestionId;

export const selectCoachedAnswerTextByQuestionId = (state: RootState) =>
    state.questionnaire.coachedAnswerTextByQuestionId;

export const selectDrawerSections = createSelector(
  [(state: RootState) => state.questionnaire.questionnaire?.questionnaireSections],
  (sections) => {
    if (!sections) return undefined;

    const mapped: IDrawerSection[] = sections.map((s) => ({
      id: s.id,
      title: s.title,
      status: s.completionStatus, // or compute below if you want dynamic status
      items: s.criteria.map((c) => {

        return {
          id: c.id,
          title: c.title,
          url: `/${s.id}/${c.id}`,
          checked: isSetDate(c.date),
          grade: c.grade as IGrade,
        };
      }),
    }));

    if (mapped.length > 0) {
      mapped[0] = { ...mapped[0], dividerBefore: true };
    }

    const introSections: IDrawerSection[] = [
      { id: "scope", title: "Introduction", status: "Not Set" as const, items: [], isIntro: true },
      { id: "additional-info", title: "Additional Information", status: "Not Set" as const, items: [], isIntro: true },
    ];

    const createImprovementSection: IDrawerSection = {
      id: "create-improvement",
      title: "Create/Link an Improvement",
      status: "Not Set" as const,
      items: [],
      isIntro: true,
      isAction: true,
    };

    return [createImprovementSection, ...introSections, ...mapped];
  }
);

export const selectSectionComputedStatus = (sectionId: string) => createSelector(
  [selectSectionById(sectionId)],
  (section): ICompletionStatus => {
    if (!section || !section.criteria?.length) return "Not Set";
    const total = section.criteria.length;
    const checked = section.criteria.filter(c => isSetDate(c.date)).length;
    if (checked === 0) return "Incomplete";
    if (checked === total) return "Complete";
    return "In Progress";
  }
);

const computeSectionStatus = (section?: IQuestionnaireSection): ICompletionStatus => {
  if (!section || !section.criteria?.length) return "Not Set";
  const total = section.criteria.length;
  const checked = section.criteria.filter(c => isSetDate(c.date)).length;
  
  if (checked === 0) return "Incomplete";
  if (checked === total) return "Complete";
  return "In Progress";
};

const normalizeDates = (q: IProductQuestionnaire) => {
  q.questionnaireSections.forEach((s) => {
    s.criteria.forEach((c) => {
      if (!(c.date instanceof Date)) {
        const d = c.date ? new Date(c.date as any) : UNSET_DATE;
        c.date = Number.isNaN(d.getTime()) ? UNSET_DATE : d;
      }
      if (!isSetDate(c.date)) c.date = UNSET_DATE;
    });
    s.completionStatus = computeSectionStatus(s);
  });
};

export default QuestionnaireSlice.reducer;
export const { setQuestionnaireSection, setActiveCriteria, setActiveQuestionnaireSection, setQuestionnaire, clearQuestionnaire, clearComments, updateCriteriaContextData, lockField, suspendCriteriaFields, lockQuestion, setIsViewingResults, clearAiAnswerPreview, revertAiAnswer, revertAllAiAnswers, pushGenerateAnswersProgressStep, clearCoachFeedbackForQuestion, setCoachProgress, hydrateCoachFeedbackFromQuestions, hydrateAiDraftsFromQuestions } = QuestionnaireSlice.actions;